import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../App.css';

interface PhotoFile {
  id: string;
  url: string;
  file: File;
}

const LoadingScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [globalProgress, setGlobalProgress] = useState(1);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const startTimeRef = useRef(Date.now());
  const uploadInitiatedRef = useRef(false);

  // Development mode detection
  const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

  const photos: PhotoFile[] = useMemo(() => {
    return location.state?.photos || [];
  }, [location.state]);

  const tasks = [
    {
      icon: '⚡️',
      title: 'Scanning your photo',
      message: 'Your identity stays private. We never share personal data with the city or anyone else.',
      duration: 30000 // 30 seconds in milliseconds
    },
    {
      icon: '🔎',
      title: 'Detecting license plate and violation',
      message: "You're helping make streets safer for everyone.",
      duration: 30000 // 30 seconds in milliseconds
    },
    {
      icon: '📍',
      title: 'Matching location and timestamp',
      message: 'Every valid report moves us closer to real accountability.',
      duration: 30000 // 30 seconds in milliseconds
    }
  ];

  // Handle global progress
  useEffect(() => {
    const updateProgress = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min((elapsed / 30000) * 100, 99);
      setGlobalProgress(progress);
    };

    updateProgress();
    const progressInterval = setInterval(updateProgress, 50);
    return () => clearInterval(progressInterval);
  }, []);

  // Handle task transitions
  useEffect(() => {
    const taskInterval = setInterval(() => {
      setCurrentTaskIndex(prevIndex => {
        if (prevIndex < tasks.length - 1) {
          return prevIndex + 1;
        }
        return prevIndex;
      });
    }, 30000 / 3);

    return () => clearInterval(taskInterval);
  }, [tasks.length]);

  // Add animation phase cycling for visual engagement
  useEffect(() => {
    const animationInterval = setInterval(() => {
      setAnimationPhase(prev => (prev + 1) % 4);
    }, 2000); // Change animation phase every 2 seconds

    return () => clearInterval(animationInterval);
  }, []);

  // Add timeout fallback
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isDevelopment) {
        console.log('Timeout reached, showing dev override option');
        setError('Processing is taking longer than expected. You can use the dev override button below.');
      } else {
        navigate('/review', { 
          state: { 
            photos,
            reportId: 'timeout-override',
            reportData: {}
          } 
        });
      }
    }, 45000); // 45 second timeout

    return () => clearTimeout(timeout);
  }, [navigate, photos, isDevelopment]);

  const handleDevOverride = useCallback(() => {
    navigate('/review', {
      state: {
        photos: photos,
        reportId: 'dev-override',
        reportData: {}
      }
    });
  }, [navigate, photos]);

  // API calls and polling logic
  useEffect(() => {
    const uploadPhotos = async () => {
      if (!photos || photos.length === 0) {
        setError('No photos to upload');
        return;
      }

      // Prevent multiple uploads
      if (uploadInitiatedRef.current) {
        return;
      }
      uploadInitiatedRef.current = true;

      setError(null);
      const formData = new FormData();
      
      photos.forEach((photo, index) => {
        formData.append(`photo${index}`, photo.file);
      });

      try {
        const createIprResponse = await fetch('https://y00ey592mh.execute-api.us-east-1.amazonaws.com/Production/illegal-parking-report', {
          method: 'POST',
          body: formData,
        });

        if (!createIprResponse.ok) {
          throw new Error(`Upload failed with status: ${createIprResponse.status}`);
        }
        const data = await createIprResponse.json();
        const reportId = data.reportId;
        
        // Poll for processing completion
        const pollIpr = async () => {
          const maxPollAttempts = 30; // 30 seconds max
          
          for (let pollAttempts = 0; pollAttempts < maxPollAttempts; pollAttempts++) {
            try {
              const getIprResponse = await fetch(`https://y00ey592mh.execute-api.us-east-1.amazonaws.com/Production/illegal-parking-report?reportId=${reportId}`);
              
              if (!getIprResponse.ok) {
                console.log('GetIllegalParkingReport API failed with status: ' + getIprResponse.status + '. Continue polling...');
                continue;
              }

              const illegalParkingReport = await getIprResponse.json();
              
              if ('READY' === illegalParkingReport.status) {
                navigate('/review', {
                  state: {
                    photos,
                    reportId,
                    reportData: illegalParkingReport
                  }
                });
                return;
              }
            } catch (pollError) {
              console.log('Poll error:', pollError); // Debug log
            } finally {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
          
          throw new Error('Processing timeout');
        };
        
        await pollIpr();
        return;
      } catch (attemptError) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
        

    };

    uploadPhotos();
  }, [photos, navigate]);

  return (
    <div className="main-content">
      <div className="loading-container">
        {/* Central Loading Animation */}
        <div className="central-loading-section">
          <div className="main-loading-spinner">
            <div className="spinner-outer-ring">
              <div className="spinner-inner-ring"></div>
            </div>
            <div className="loading-pulse" style={{ 
              animationDelay: `${animationPhase * 0.2}s` 
            }}></div>
          </div>
          <div className="loading-title">
            Processing your report...
          </div>
          <div className="loading-subtitle">
            {tasks[currentTaskIndex]?.title || 'Working on it'}
          </div>
        </div>

        <div className="progress-container">
          <div className="global-progress">
            <div 
              className="global-progress-fill" 
              style={{ width: `${Math.max(1, Math.min(globalProgress, 99))}%` }} 
            />
          </div>
          <div className="progress-percentage">{Math.round(globalProgress)}%</div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {isDevelopment && (
          <div className="dev-controls">
            <div className="dev-controls-buttons">
              <button className="dev-button danger" onClick={handleDevOverride}>
                Dev Override: Skip to Review
              </button>
              <button className="dev-button" onClick={() => setShowDebugInfo(!showDebugInfo)}>
                Toggle Debug Info
              </button>
            </div>
            {showDebugInfo && (
              <div className="debug-info">
                <div>Current Task: {currentTaskIndex}</div>
                <div>Global Progress: {Math.round(globalProgress)}%</div>
                <div>Time Elapsed: {Math.round((Date.now() - startTimeRef.current) / 1000)}s</div>
                <div>Photos Count: {photos.length}</div>
                {error && <div>Error: {error}</div>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingScreen;
